import { useCallback, useEffect, useMemo, useState } from 'react'

import { ICardListGroupConfig } from '~/client/src/shared/components/CardList/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { useCardListCore } from '~/client/src/shared/hooks/useCardListCore'

import {
  IListSelectorGroupConfig,
  IListSelectorItem,
  IListSelectorSearchConfig,
  IListSelectorSortConfig,
  SelectionMode,
} from './types'

interface IUseListSelectorStateOptions<T extends IListSelectorItem> {
  items: T[]
  selectedIds: Set<string>
  onItemToggle?: (id: string) => void
  onToggleAll?: (ids: string[], selectAll: boolean) => void
  showActions?: boolean
  selectionMode?: SelectionMode
  onClose?: () => void
  search?: IListSelectorSearchConfig<T>
  sort?: IListSelectorSortConfig<T>
  grouping?: IListSelectorGroupConfig<T>
  isItemDisabled?: (item: T) => boolean
}

export function useListSelectorState<T extends IListSelectorItem>(
  options: IUseListSelectorStateOptions<T>,
) {
  const {
    items,
    selectedIds,
    onItemToggle,
    onToggleAll,
    showActions,
    selectionMode = 'multi',
    onClose,
    search,
    sort,
    grouping,
    isItemDisabled,
  } = options

  const isSingle = selectionMode === 'single'

  const initialSortKey = sort?.defaultKey ?? ''
  const initialSortOrder = SortOrder.ASC
  const initialGroupingKey = grouping?.defaultKey ?? null

  const [searchKey, setSearchKey] = useState('')
  const [sortKey, setSortKey] = useState<string>(initialSortKey)
  const [sortOrder, setSortOrder] = useState<SortOrder>(initialSortOrder)
  const [groupingKey, setGroupingKey] = useState<string | null>(
    initialGroupingKey,
  )

  const effectiveShowActions = isSingle ? false : showActions

  // Draft mode for showActions
  const [draftIds, setDraftIds] = useState<Set<string>>(
    () => new Set(selectedIds),
  )

  useEffect(() => {
    if (effectiveShowActions) setDraftIds(new Set(selectedIds))
  }, [selectedIds, effectiveShowActions])

  const effectiveIds = effectiveShowActions ? draftIds : selectedIds

  const internalToggle = useCallback((id: string) => {
    setDraftIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const internalToggleAll = useCallback((ids: string[], selectAll: boolean) => {
    setDraftIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => (selectAll ? next.add(id) : next.delete(id)))
      return next
    })
  }, [])

  const singleToggle = useCallback(
    (id: string) => {
      onItemToggle?.(id)
      onClose?.()
    },
    [onItemToggle, onClose],
  )

  const effectiveToggle = isSingle
    ? singleToggle
    : effectiveShowActions
      ? internalToggle
      : onItemToggle
  const effectiveToggleAll = isSingle
    ? undefined
    : effectiveShowActions
      ? internalToggleAll
      : onToggleAll

  // Filter + sort items
  const processedItems = useMemo(() => {
    let result = items

    if (searchKey && search) {
      const lower = searchKey.toLowerCase()
      result = result.filter(item => search.searchFn(item, lower))
    }

    if (sort) {
      result = [...result].sort((a, b) => sort.sortFn(a, b, sortOrder))
    }

    return result
  }, [items, searchKey, search, sort, sortOrder])

  // Group config
  const groupConfig = useMemo((): ICardListGroupConfig<T> | undefined => {
    if (!grouping) return undefined
    // "None" is flat unless the caller opted into a base grouping to fall back on.
    if (!groupingKey) return grouping.ungroupedConfig
    return grouping.buildGroupConfig(groupingKey)
  }, [grouping, groupingKey])

  const listState = useCardListCore({
    items: processedItems,
    groupConfig,
    defaultCollapsed: grouping?.defaultCollapsed,
  })

  const pickableItems = useMemo(
    () =>
      isItemDisabled
        ? processedItems.filter(item => !isItemDisabled(item))
        : processedItems,
    [processedItems, isItemDisabled],
  )

  const hasItems = pickableItems.length > 0

  const areAllSelected = useMemo(() => {
    if (isSingle || !pickableItems.length) return false
    return pickableItems.every(item => effectiveIds.has(item.id))
  }, [isSingle, pickableItems, effectiveIds])

  const isAnySelected = useMemo(
    () => !isSingle && pickableItems.some(item => effectiveIds.has(item.id)),
    [isSingle, pickableItems, effectiveIds],
  )

  // The whole selection: Apply commits ids the search or a wrapper filter hides.
  const selectedCount = effectiveIds.size

  const handleToggleAll = useCallback(() => {
    if (isSingle) return
    // Built here rather than memoized: only the click path needs the array.
    const ids = pickableItems.map(item => item.id)
    if (effectiveToggleAll) {
      effectiveToggleAll(ids, !areAllSelected)
    } else if (effectiveToggle) {
      ids.forEach(id => effectiveToggle(id))
    }
  }, [
    isSingle,
    pickableItems,
    areAllSelected,
    effectiveToggleAll,
    effectiveToggle,
  ])

  const handleGroupChange = useCallback((key: string | null) => {
    setGroupingKey(key)
  }, [])

  const handleSortChange = useCallback((key: string | null) => {
    if (key) setSortKey(key)
  }, [])

  const handleSortOrderChange = useCallback((order: SortOrder) => {
    setSortOrder(order)
  }, [])

  return {
    selectionMode,
    searchKey,
    setSearchKey,
    sortKey,
    sortOrder,
    groupingKey,
    groupConfig,
    listState,
    processedItems,
    effectiveIds,
    effectiveToggle,
    effectiveToggleAll,
    draftIds,
    hasItems,
    areAllSelected,
    isAnySelected,
    selectedCount,
    handleToggleAll,
    handleGroupChange,
    handleSortChange,
    handleSortOrderChange,
    initialSortKey,
    initialSortOrder,
    initialGroupingKey,
  }
}
